/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import TextModal from './TextModal';
import { LayoutContext } from '../stores/LayoutStore';
import { getFrontendSettingsGql } from '../screens/checkout/gql';

export default observer(() => {
  const { t } = useTranslation('texts');
  const [showModal, setShowModal] = useState(false);
  const [type, setType] = useState(null);
  const layoutContext = useContext(LayoutContext);

  const { data } = useQuery(getFrontendSettingsGql, {
    skip: !layoutContext?.slug,
    variables: {
      slug: layoutContext?.slug,
    },
  });

  const footerLinks = useMemo(
    () => data?.getDetails?.event?.frontendSettings?.footerLinks || [],
    [data?.getDetails?.event?.frontendSettings?.footerLinks],
  );

  const hasFooterLinks = footerLinks.length > 0;

  return (
    <Footer>
      <Grid container>
        <Grid item xs={12}>
          <Flex>
            {hasFooterLinks ? (
              footerLinks.map((link) => (
                <FlexItem key={`${link.name}-${link.link}`}>
                  <Link
                    href={link.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography variant="subtitle2">
                      {link.name}
                    </Typography>
                  </Link>
                </FlexItem>
              ))
            ) : (
              <>
                <FlexItem>
                  <Link
                    onClick={() => {
                      setType('imprint');
                      setShowModal(true);
                    }}
                  >
                    <Typography variant="subtitle2">
                      {t('imprint')}
                    </Typography>
                  </Link>
                </FlexItem>

                <FlexItem>
                  <Link
                    onClick={() => {
                      setType('privacyPolicy');
                      setShowModal(true);
                    }}
                  >
                    <Typography variant="subtitle2" style={{ cursor: 'pointer' }}>
                      {t('privacyPolicy')}
                    </Typography>
                  </Link>
                </FlexItem>

                <FlexItem>
                  <Link
                    onClick={() => {
                      setType('toc');
                      setShowModal(true);
                    }}
                  >
                    <Typography variant="subtitle2" style={{ cursor: 'pointer' }}>
                      {t('toc')}
                    </Typography>
                  </Link>
                </FlexItem>

                <FlexItem>
                  <Link
                    onClick={() => {
                      setType('rightOfWithdrawal');
                      setShowModal(true);
                    }}
                  >
                    <Typography variant="subtitle2" style={{ cursor: 'pointer' }}>
                      {t('rightOfWithdrawal')}
                    </Typography>
                  </Link>
                </FlexItem>

                <FlexItem>
                  <Link
                    href="https://www.wirkung.group/digital"
                    target="_blank"
                    rel="noreferrer"
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    <Typography variant="subtitle2">
                      Technik
                    </Typography>
                  </Link>
                </FlexItem>
              </>
            )}
          </Flex>
        </Grid>
      </Grid>
      <TextModal open={showModal} setOpen={setShowModal} type={type} />
    </Footer>
  );
});

const Footer = styled('section')(() => ({
  marginTop: 'auto',
  paddingTop: '50px',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  // Avoid clipping with third party bottom content like arena chat.
  marginBottom: 32,
}));

const Flex = styled('div')(({ theme }) => ({
  margin: 0,
  display: 'flex',
  flexFlow: 'column wrap',
  justifyContent: 'center',
  padding: '10px',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const FlexItem = styled('div')(({ theme }) => ({
  flexBasis: 'auto',
  textAlign: 'center',
  padding: '10px',
  borderLeft: 'solid 0px white',
  [theme.breakpoints.up('sm')]: {
    borderLeft: 'solid 0px white',
  },
}));

const Link = styled('a')(({ theme }) => ({
  color: theme.palette.background.contrastText || '#000',
  '&:hover': {
    color: theme.palette.background.hover || '#ccc',
  },
  fontSize: '16px',
  fontWeight: 400,
  cursor: 'pointer',
  textDecoration: 'underline',
}));
